import { create } from 'zustand';

// biome-ignore lint/suspicious/noEmptyInterface: declaration-merging target — features augment this interface
export interface SheetRegistry {}

export type SheetProps = SheetRegistry[keyof SheetRegistry];

interface SheetState {
    openSheets: SheetProps[];
    currentSheet: SheetProps | undefined;
    openSheet: (props: SheetProps) => void;
    closeCurrentSheet: () => void;
    closeAllSheets: () => void;
    goBack: (steps?: number) => void;
    // `null` drops that entry instead of replacing it — how a hand-back closes one entry by type
    // without assuming it is the current one.
    replaceSheets: (map: (sheet: SheetProps) => SheetProps | null) => void;
}

export const useSheetStore = create<SheetState>((set) => ({
    openSheets: [],
    currentSheet: undefined,
    // A copy, so each opening is its own object even when a caller passes the same props twice —
    // `useSaveThenGoBack` tells openings apart by that identity.
    openSheet: (props) => {
        const sheet = { ...props };
        set((state) => ({
            currentSheet: sheet,
            openSheets: [...state.openSheets, sheet],
        }));
    },
    closeCurrentSheet: () => {
        set((state) => {
            const openSheets = state.openSheets.slice(0, -1);
            return {
                openSheets,
                currentSheet: openSheets.at(-1),
            };
        });
    },
    closeAllSheets: () => {
        set({
            openSheets: [],
            currentSheet: undefined,
        });
    },
    goBack: (steps = 1) => {
        if (steps <= 0) return;
        set((state) => {
            const stepsToRemove = Math.min(steps, state.openSheets.length);
            const openSheets = state.openSheets.slice(0, -stepsToRemove);
            return {
                openSheets,
                currentSheet: openSheets.at(-1),
            };
        });
    },
    replaceSheets: (map) => {
        set((state) => {
            const openSheets = state.openSheets.flatMap((sheet) => {
                const next = map(sheet);
                return next ? [next] : [];
            });
            return {
                openSheets,
                currentSheet: openSheets.at(-1),
            };
        });
    },
}));
